import {
  SiteAssignmentEntity,
  siteAssignmentSelector,
  SiteAssignmentType,
  siteAssignmentTypes,
  SiteEntity,
  toTransportCategory,
  transportCategory,
  TransportCategory,
  useDeleteSiteAssignmentMutation,
  useGetCompanySiteAssignmentBySiteQuery,
  useGetDeliverySiteSiteAssignmentBySiteQuery,
  useGetPickupSiteSiteAssignmentBySiteQuery
} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {ActionIcon, Card, Container, Grid, Group, Stack, Tabs, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {IconEdit, IconPlus, IconTrash} from "@tabler/icons-react";
import {modals} from "@mantine/modals";
import {notifications} from "@mantine/notifications";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {getTransportCategoryColorRefByType} from "../../../util/transport-category-ui-util";
import {
  SiteAssignmentCompanyAssocDataTable
} from "../../data-table/SiteAssignmentDataTable/SiteAssignmentCompanyAssocDataTable";
import {
  SiteAssignmentSiteAssocDataTable
} from "../../data-table/SiteAssignmentDataTable/SiteAssignmentSiteAssocDataTable";
import {Empty} from "../../empty/Empty/Empty";
import { HasAccess } from "../../auth/HasAccess/HasAccess";

export type SiteAssignmentsProps = {
  site: SiteEntity,
}

export const SiteAssignments = (props: SiteAssignmentsProps) => {
  const {site} = props;

  const navigate = useNavigate()
  const {pathname, search, state} = useLocation();
  const {regionId} = useApp();
  const {t} = useTranslation(["translation", "common"]);

  const appCategory = useMemo(() => {
    return state?.category ? toTransportCategory(state.category) : transportCategory.water
  }, [state?.category])

  return (
    <Grid>

      <HasAccess permissions={["site_assignment_company_view"]}>
        <Grid.Col span={12}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group position="apart" pb="sm">
              <Title order={4}>
                {t("profile.site.assignments.section.company.label", "Company Assignments")}
              </Title>
            </Group>

            <Tabs
              defaultValue={appCategory.code}
              onTabChange={(value) => navigate(pathname + search, {
                replace: true,
                state: {...state, tabValue: value}
              })}>

              <AssignmentTabs/>

              <Tabs.Panel value={transportCategory.water.code}>
                <CompanyAssignment
                  regionId={regionId ?? "-1"}
                  siteId={site.id}
                  category={transportCategory.water}/>
              </Tabs.Panel>

              <Tabs.Panel value={transportCategory.oil.code}>
                <CompanyAssignment
                  regionId={regionId ?? "-1"}
                  siteId={site.id}
                  category={transportCategory.oil}/>
              </Tabs.Panel>
            </Tabs>
          </Card>
        </Grid.Col>
      </HasAccess>

      <HasAccess permissions={["site_assignment_site_view"]}>
        <Grid.Col span={12}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group position="apart" pb="sm">
              <Title order={4}>
                {t("profile.site.assignments.section.delivery.label", "Delivery Site Assignments")}
              </Title>
            </Group>

            <Tabs defaultValue={appCategory.code}>
              <AssignmentTabs/>

              <Tabs.Panel value={transportCategory.water.code}>
                <DeliverySiteAssignment
                  regionId={regionId ?? "-1"}
                  siteId={site.id}
                  category={transportCategory.water}/>
              </Tabs.Panel>

              <Tabs.Panel value={transportCategory.oil.code}>
                <DeliverySiteAssignment
                  regionId={regionId ?? "-1"}
                  siteId={site.id}
                  category={transportCategory.oil}/>
              </Tabs.Panel>
            </Tabs>
          </Card>
        </Grid.Col>
      </HasAccess>

      <HasAccess permissions={["site_assignment_site_view"]}>
        <Grid.Col span={12}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group position="apart" pb="sm">
              <Title order={4}>
                {t("profile.site.assignments.section.pickup.label", "Pickup Site Assignments")}
              </Title>
            </Group>

            <Tabs defaultValue={appCategory.code}>
              <AssignmentTabs/>

              <Tabs.Panel value={transportCategory.water.code}>
                <PickupSiteAssignment
                  regionId={regionId ?? "-1"}
                  siteId={site.id}
                  category={transportCategory.water}/>
              </Tabs.Panel>

              <Tabs.Panel value={transportCategory.oil.code}>
                <PickupSiteAssignment
                  regionId={regionId ?? "-1"}
                  siteId={site.id}
                  category={transportCategory.oil}/>
              </Tabs.Panel>
            </Tabs>
          </Card>
        </Grid.Col>
      </HasAccess>
    </Grid>
  );
};

type AssignmentTabProps = {
  category: TransportCategory
};

const AssignmentTab = (props: AssignmentTabProps) => {
  const {category} = props;

  const {t} = useTranslation(["translation", "common"]);

  return (
    <Tabs.Tab
      color={getTransportCategoryColorRefByType(category)}
      value={category.code}>
      {t(category.displayValue.id, category.displayValue.defaultMessage, {ns: "common"})}
    </Tabs.Tab>
  );
};

type AssignmentTabsProps = {
  // props here
};

const AssignmentTabs = (props: AssignmentTabsProps) => {
  return (
    <Tabs.List>
      <AssignmentTab category={transportCategory.water}/>
      <AssignmentTab category={transportCategory.oil}/>
    </Tabs.List>
  );
};

type CompanyAssignmentProps = {
  regionId: string;
  siteId: string
  category: TransportCategory
}

const CompanyAssignment = (props: CompanyAssignmentProps) => {
  const {regionId, siteId, category} = props;

  const navigate = useNavigate()
  const {t} = useTranslation(["translation", "common"]);

  const [deleteSiteAssignment, {isLoading: isDeleting}] = useDeleteSiteAssignmentMutation();

  const selectSiteAssignment = useMemo(() => {
    return siteAssignmentSelector();
  }, []);

  const {siteAssignment, isLoading} = useGetCompanySiteAssignmentBySiteQuery({
    regionId: regionId,
    siteId: siteId,
    category: category.code
  }, {
    selectFromResult: (result) => ({
      siteAssignment: selectSiteAssignment(result),
      isLoading: result.isLoading
    })
  });

  const onNewClick = useCallback(() => {
    navigate("assignments/company/form", {
      state: {
        siteId: siteAssignment?.siteId ?? siteId,
        type: siteAssignment?.type ?? siteAssignmentTypes.company,
        category: siteAssignment?.category ?? category,
      }
    });
  }, [navigate, siteAssignment, siteId, category])

  const onEditClick = useCallback(() => {
    navigate("assignments/company/form", {
      state: {
        id: siteAssignment?.id,
        siteId: siteAssignment?.siteId ?? siteId,
        type: siteAssignment?.type ?? siteAssignmentTypes.company,
        category: siteAssignment?.category ?? category,
      }
    });
  }, [navigate, siteAssignment, siteId, category])

  const onDelete = useCallback(() => {
    if (siteAssignment) {
      deleteSiteAssignment(siteAssignment)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Site Assignment Deleted",
            message: ""
          });
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [siteAssignment, deleteSiteAssignment, t])

  const openDeleteModal = useCallback(() => {
    modals.openConfirmModal({
      title: 'Delete Site Assignment',
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete this site assignment?
        </Text>
      ),
      labels: {confirm: 'Delete Site Assignment', cancel: "Cancel"},
      confirmProps: {color: 'red'},
      onConfirm: onDelete,
    })
  }, [onDelete]);

  return (
    <Container p="md" fluid={true}>
      <Stack>
        {siteAssignment ? (
          <>
            <Grid>
              <Grid.Col span={6}>
                <Text ta="left" fz="sm" weight={500} mt="md">
                  {t("profile.site.assignments.field.strategy.label", "Strategy")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  {siteAssignment.strategy.code}
                </Text>
              </Grid.Col>
            </Grid>

            {siteAssignment.companyAssocs && siteAssignment.companyAssocs.length > 0 ? (
              <SiteAssignmentCompanyAssocDataTable
                withBorder
                records={siteAssignment.companyAssocs ?? []}
                fetching={isLoading}
              />
            ) : (
              <Empty message={"No Assignments"} />
            )}
          </>
        ) : (
          <Empty message={"No Assignments"} />
        )}

        <HasAccess permissions={["site_assignment_edit"]}>
          <Group spacing={4} position="left" noWrap>
            {!siteAssignment && (
              <ActionIcon color="green" onClick={() => onNewClick()}>
                <IconPlus size={16}/>
              </ActionIcon>
            )}
            {siteAssignment && (
              <>
                <ActionIcon color="blue" onClick={() => onEditClick()}>
                  <IconEdit size={16}/>
                </ActionIcon>
                <ActionIcon color="red" onClick={openDeleteModal} loading={isDeleting}>
                  <IconTrash size={16}/>
                </ActionIcon>
              </>
            )}
          </Group>
        </HasAccess>
      </Stack>
    </Container>
  );
};

type SiteAssignmentProps = {
  siteAssignment?: SiteAssignmentEntity
  siteId: string;
  type: SiteAssignmentType
  category: TransportCategory
  isLoading?: boolean
}

const SiteAssignment = (props: SiteAssignmentProps) => {
  const {
    siteAssignment,
    siteId,
    type,
    category,
    isLoading = false,
  } = props;

  const navigate = useNavigate()
  const {t} = useTranslation(["translation", "common"]);

  const [deleteSiteAssignment, {isLoading: isDeleting}] = useDeleteSiteAssignmentMutation();

  const onNewClick = useCallback(() => {
    navigate("assignments/site/form", {
      state: {
        siteId: siteAssignment?.siteId ?? siteId,
        type: siteAssignment?.type ?? type,
        category: siteAssignment?.category ?? category,
      }
    });
  }, [navigate, siteAssignment, siteId, type, category])

  const onEditClick = useCallback(() => {
    navigate("assignments/site/form", {
      state: {
        id: siteAssignment?.id,
        siteId: siteAssignment?.siteId ?? siteId,
        type: siteAssignment?.type ?? type,
        category: siteAssignment?.category ?? category,
      }
    });
  }, [navigate, siteAssignment, siteId, type, category])

  const onDelete = useCallback(() => {
    if (siteAssignment) {
      deleteSiteAssignment(siteAssignment)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Site Assignment Deleted",
            message: ""
          });
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [siteAssignment, deleteSiteAssignment, t])

  const openDeleteModal = useCallback(() => {
    modals.openConfirmModal({
      title: 'Delete Site Assignment',
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete this site assignment?
        </Text>
      ),
      labels: {confirm: 'Delete Site Assignment', cancel: "Cancel"},
      confirmProps: {color: 'red'},
      onConfirm: onDelete,
    })
  }, [onDelete]);

  return (
    <Container p="md" fluid={true}>
      <Stack>
        {siteAssignment ? (
          <>
            <Grid>
              <Grid.Col span={6}>
                <Text ta="left" fz="sm" weight={500} mt="md">
                  {t("profile.site.assignments.field.strategy.label", "Strategy")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  {siteAssignment.strategy.code}
                </Text>
              </Grid.Col>
            </Grid>

            {siteAssignment.siteAssocs && siteAssignment.siteAssocs.length > 0 ? (
              <SiteAssignmentSiteAssocDataTable
                withBorder
                records={siteAssignment.siteAssocs ?? []}
                fetching={isLoading}
              />
            ) : (
              <Empty message={"No Assignments"} />
            )}
          </>
        ) : (
          <Empty message={"No Assignments"} />
        )}

        <HasAccess permissions={["site_assignment_edit"]}>
          <Group spacing={4} position="left" noWrap>
            {!siteAssignment && (
              <ActionIcon color="green" onClick={() => onNewClick()}>
                <IconPlus size={16}/>
              </ActionIcon>
            )}
            {siteAssignment && (
              <>
                <ActionIcon color="blue" onClick={() => onEditClick()}>
                  <IconEdit size={16}/>
                </ActionIcon>
                <ActionIcon color="red" onClick={openDeleteModal} loading={isDeleting}>
                  <IconTrash size={16}/>
                </ActionIcon>
              </>
            )}
          </Group>
        </HasAccess>
      </Stack>
    </Container>
  );
};

type PickupSiteAssignmentProps = {
  regionId: string;
  siteId: string;
  category: TransportCategory;
}

const PickupSiteAssignment = (props: PickupSiteAssignmentProps) => {
  const {regionId, siteId, category} = props;

  const selectSiteAssignment = useMemo(() => {
    return siteAssignmentSelector();
  }, []);

  const {siteAssignment, isSiteAssignmentLoading} = useGetPickupSiteSiteAssignmentBySiteQuery({
    regionId: regionId,
    siteId: siteId,
    category: category.code
  }, {
    selectFromResult: (result) => ({
      siteAssignment: selectSiteAssignment(result),
      isSiteAssignmentLoading: result.isLoading
    })
  });

  return (
    <SiteAssignment
      siteAssignment={siteAssignment}
      siteId={siteId}
      type={siteAssignmentTypes.pickupSite}
      category={category}
      isLoading={isSiteAssignmentLoading}/>
  );
};

type DeliverySiteAssignmentProps = {
  regionId: string;
  siteId: string;
  category: TransportCategory;
}

const DeliverySiteAssignment = (props: DeliverySiteAssignmentProps) => {
  const {regionId, siteId, category} = props;

  const selectSiteAssignment = useMemo(() => {
    return siteAssignmentSelector();
  }, []);

  const {siteAssignment, isSiteAssignmentLoading} = useGetDeliverySiteSiteAssignmentBySiteQuery({
    regionId: regionId,
    siteId: siteId,
    category: category.code
  }, {
    selectFromResult: (result) => ({
      siteAssignment: selectSiteAssignment(result),
      isSiteAssignmentLoading: result.isLoading
    })
  });

  return (
    <SiteAssignment
      siteAssignment={siteAssignment}
      siteId={siteId}
      type={siteAssignmentTypes.deliverySite}
      category={category}
      isLoading={isSiteAssignmentLoading}/>
  );
};
